import { useAuth } from '../hooks/useAuth'
import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Navigation from '../components/Navigation'
import { apiClient, type UserGameScore } from '../services/apiClient'

type SortField = 'score' | 'achievedAt' | 'location' | string // string for dynamic metadata fields
type SortDirection = 'asc' | 'desc'

export default function GameScoresPage() {
  const { user } = useAuth()
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const [scores, setScores] = useState<UserGameScore[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [gameName, setGameName] = useState<string>('')
  const [sortField, setSortField] = useState<SortField>('score')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const getSortedScores = () => {
    return [...scores].sort((a, b) => {
      let aValue: any, bValue: any

      if (sortField === 'score') {
        aValue = a.score
        bValue = b.score
      } else if (sortField === 'achievedAt') {
        aValue = new Date(a.achievedAt).getTime()
        bValue = new Date(b.achievedAt).getTime()
      } else if (sortField === 'location') {
        aValue = a.location.toLowerCase()
        bValue = b.location.toLowerCase()
      } else {
        // Dynamic metadata field
        aValue = a.metadata?.[sortField] ?? ''
        bValue = b.metadata?.[sortField] ?? ''
        // Convert to lowercase for string comparison
        if (typeof aValue === 'string') aValue = aValue.toLowerCase()
        if (typeof bValue === 'string') bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }

  const SortableHeader = ({ field, children }: { field: SortField, children: React.ReactNode }) => (
    <th 
      className="text-left py-3 px-2 text-gray-300 font-medium cursor-pointer hover:text-white transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          <span className="text-firedup-orange">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  )

  useEffect(() => {
    const fetchGameScores = async () => {
      if (!gameId) {
        setError('No game ID provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const gameScores = await apiClient.getUserGameScores(parseInt(gameId, 10))
        setScores(gameScores)
        
        // Set game name from the first score if available
        if (gameScores.length > 0) {
          setGameName(gameScores[0].gameName)
        }
      } catch (err) {
        console.error('Error fetching game scores:', err)
        setError(err instanceof Error ? err.message : 'Failed to load game scores')
      } finally {
        setLoading(false)
      }
    }

    if (user && gameId) {
      fetchGameScores()
    }
  }, [user, gameId])

  // This shouldn't happen due to route protection, but adding safety check
  if (!user) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Redirecting...</div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-firedup-orange text-xl">Loading your scores...</div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">Error loading scores</div>
            <div className="text-gray-300 mb-4">{error}</div>
          </div>
        </div>
      </div>
    )
  }

  const topScore = scores.length > 0 ? Math.max(...scores.map(s => s.score)) : 0
  const averageScore = scores.length > 0 ? Math.round(scores.reduce((sum, s) => sum + s.score, 0) / scores.length) : 0

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black text-white mb-2">
            Your Scores: <span className="text-firedup-orange">{gameName || `Game ${gameId}`}</span>
          </h1>
          <p className="text-xl text-gray-300">
            All your recorded scores for this game
          </p>
        </div>

        {/* Statistics */}
        {scores.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
              <h3 className="text-lg font-bold text-white mb-2">🏆 Your Best Score</h3>
              <div className="text-3xl font-bold text-firedup-orange">
                {topScore.toLocaleString()}
              </div>
            </div>
            
            <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
              <h3 className="text-lg font-bold text-white mb-2">📊 Average Score</h3>
              <div className="text-3xl font-bold text-firedup-orange">
                {averageScore.toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {/* Scores Table */}
        <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
          <h3 className="text-xl font-bold text-white mb-6">All Scores ({scores.length})</h3>
          
          {scores.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-firedup-light-gray">
                    <SortableHeader field="score">Score</SortableHeader>
                    <SortableHeader field="achievedAt">Date</SortableHeader>
                    {/* Dynamic metadata columns (excluding name, score, gameId, location, and id) */}
                    {scores.length > 0 && scores[0].metadata && Object.keys(scores[0].metadata).filter(key => key !== 'name' && key !== 'score' && key !== 'gameId' && key !== 'location' && key !== 'id').map(key => (
                      <SortableHeader key={key} field={key}>
                        <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                      </SortableHeader>
                    ))}
                    <SortableHeader field="location">Location</SortableHeader>
                    <th className="text-center py-3 px-2 text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedScores().map((score) => (
                    <tr key={score.scoreId} className="border-b border-firedup-light-gray/30 hover:bg-firedup-dark/30 transition-colors">
                      <td className="py-3 px-2">
                        <div className="text-lg font-bold text-white">
                          {score.score.toLocaleString()}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-gray-300">
                        <div>{new Date(score.achievedAt).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-400">{new Date(score.achievedAt).toLocaleTimeString()}</div>
                      </td>
                      {/* Dynamic metadata values (excluding name, score, gameId, and location) */}
                      {scores.length > 0 && scores[0].metadata && Object.keys(scores[0].metadata).filter(key => key !== 'name' && key !== 'score' && key !== 'gameId' && key !== 'location' && key !== 'id').map(key => (
                        <td key={key} className="py-3 px-2 text-gray-300">
                          {score.metadata && score.metadata[key] !== undefined ? (
                            // Format accuracy as percentage
                            typeof score.metadata[key] === 'number' && key === 'accuracy' ? 
                              `${(score.metadata[key] * 100).toFixed(1)}%` :
                            // Format time-related fields with seconds
                            typeof score.metadata[key] === 'number' && key === 'timeAlive' ?
                              `${score.metadata[key]}s` :
                            // Default formatting
                            String(score.metadata[key])
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                      ))}
                      <td className="py-3 px-2 text-gray-300">{score.location}</td>
                      <td className="py-3 px-2 text-center">
                        {score.matchId && (
                          <button
                            onClick={() => navigate(`/match-details/${score.matchId}`)}
                            className="px-3 py-1 bg-firedup-orange hover:bg-firedup-orange/80 text-white text-xs font-medium rounded transition-colors duration-200"
                          >
                            Details
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-lg mb-2">🎮 No scores found!</div>
              <div className="text-gray-500">Play this game to see your scores here</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
